import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, AlertTriangle, Scale, RefreshCcw, FileText,
  Mail, CreditCard, Users, ArrowLeft, Shield, ShieldCheck, Menu, X, Building2, Target,
  Sparkles, Calendar, DollarSign, NotebookPen, Inbox, Mailbox, Activity, BarChart3, Crown, MapPin, BookOpen, MousePointerClick, HeartPulse, Share2, ScanSearch
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin navigation items
const adminNavItems = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard },
  { path: '/admin/creator-health', label: 'Creator Health', icon: Crown },
  { path: '/admin/partner-applications', label: 'Partner Applications', icon: Building2 },
  { path: '/admin/build-partner-applications', label: 'Build Partners', icon: Building2 },
  { path: '/admin/optimizer-funnel', label: 'Optimizer Funnel', icon: BarChart3 },
  { path: '/admin/memes', label: 'Viral Memes', icon: Sparkles },
  { path: '/admin/affiliate-partners', label: 'Affiliate Partners', icon: Sparkles },
  { path: '/admin/products-manager', label: 'Product Manager', icon: Sparkles },
  { path: '/admin/fix-neon-products', label: 'Fix Neon Products', icon: ScanSearch },
  { path: '/admin/share-cards', label: 'Product Share Cards', icon: Share2 },
  { path: '/admin/house-products/weekly', label: 'House Products (Weekly)', icon: Sparkles },
  { path: '/admin/wave2-ops', label: 'Wave 2 Prod Sync', icon: Sparkles },
  { path: '/admin/partner-revenue', label: 'Partner Revenue', icon: DollarSign },
  { path: '/admin/commission-feed', label: 'Commission Feed', icon: Mailbox },
  { path: '/admin/spotlight-calendar', label: 'Spotlight Calendar', icon: Calendar },
  { path: '/admin/prospects', label: 'Prospects (Outreach)', icon: Target },
  { path: '/admin/lead-finder', label: 'Lead Finder (Direct Mail)', icon: MapPin },
  { path: '/admin/click-analytics', label: 'Click Analytics', icon: MousePointerClick },
  { path: '/admin/lifecycle-emails', label: 'Lifecycle Emails', icon: Mail },
  { path: '/admin/health-checks', label: 'Health Checks', icon: HeartPulse },
  { path: '/admin/playbooks', label: 'Playbooks', icon: BookOpen },
  { path: '/admin/fraud', label: 'Fraud Alerts', icon: AlertTriangle },
  { path: '/admin/disputes', label: 'Disputes', icon: Scale },
  { path: '/admin/refunds', label: 'Refunds', icon: RefreshCcw },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: FileText },
  { path: '/admin/security/blocked-ips', label: 'IP Blocklist', icon: ShieldCheck },
  { path: '/admin/vetting-log', label: 'Auto-Vetting Log', icon: ShieldCheck },
  { path: '/admin/compliance', label: 'Compliance Watch', icon: Shield },
  { path: '/admin/influencers', label: 'Influencers', icon: Sparkles },
  { path: '/admin/manual-payouts', label: 'International Payouts', icon: DollarSign },
  { path: '/admin/email-delivery', label: 'Email Delivery', icon: Mail },
  { path: '/admin/checkout-failures', label: 'Checkout Failures', icon: CreditCard },
  { path: '/admin/platform-partners', label: 'Platform Partners', icon: Building2 },
  { path: '/admin/upgrade-candidates', label: 'Upgrade Candidates', icon: Target },
  { path: '/admin/outreach-log', label: 'Outreach Log', icon: Inbox },
  { path: '/admin/outreach', label: 'Outreach Templates', icon: Mail },
  { path: '/admin/seo-audits', label: 'SEO Audit Trends', icon: Activity },
  { path: '/admin/inventor-notebook', label: "Inventor's Notebook", icon: NotebookPen },
];

function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to access admin panel');
        navigate('/');
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.is_admin) {
        setIsAdmin(true);
      } else {
        toast.error('Admin access required');
        navigate('/');
      }
    } catch (error) {
      toast.error('Admin access required');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Site
            </Button>
            <div className="hidden md:flex items-center gap-2">
              <Shield className="w-5 h-5 text-white" />
              <span className="font-bold text-lg">Admin Console</span>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          
          {/* Legacy admin link - Desktop */}
          <Link
            to="/admin-legacy"
            className="hidden md:block text-sm text-neutral-400 hover:text-white"
          >
            Legacy Dashboard →
          </Link>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:block w-64 min-h-[calc(100vh-73px)] border-r border-white/10 bg-neutral-950/50">
          <nav className="p-4 space-y-1">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-white text-black border border-white/30'
                      : 'text-neutral-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/80" onClick={() => setMobileMenuOpen(false)} />
            <aside className="absolute left-0 top-0 w-64 h-full bg-neutral-950 border-r border-white/10 flex flex-col">
              {/* Scrollable nav body — height capped to screen so the
                  Legacy Dashboard link at the bottom is always reachable
                  even on small phones with 12+ menu items. */}
              <nav
                className="flex-1 overflow-y-auto overscroll-contain px-4 pt-16 pb-4 space-y-1"
                style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
              >
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-white text-black'
                          : 'text-neutral-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    </Link>
                  );
                })}

                {/* Legacy Dashboard Link — pinned to end of scroll list */}
                <div className="pt-3 mt-3 border-t border-white/10">
                  <Link
                    to="/admin-legacy"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
                    data-testid="mobile-legacy-dashboard-link"
                  >
                    <Shield className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-medium">Legacy Dashboard</span>
                  </Link>
                </div>
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content
            `min-w-0` is critical: without it, a child wider than the
            viewport (e.g. the Partner Revenue table on mobile) makes
            this flex item grow to match and shoves all sibling content
            off the right edge. `overflow-x-hidden` is the belt-and-
            braces guard for any other overflow source we don't catch. */}
        <main className="flex-1 min-w-0 overflow-x-hidden p-4 md:p-6 min-h-[calc(100vh-73px)]">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
